import _ from "lodash";
import { BaseController } from "../../common/base/controller";
import { NewPortfolioModel } from "./newPortfolio.model";
import { OldBankModel } from "../oldPortfolio/bank.model";
import { NewBankModel } from "../start/bank.model";
import { OldPortfolioModel } from "../oldPortfolio/oldPortfolio.model";

export class NewPortfolioController extends BaseController {
	loading = false;
	oldBankForm;
	oldPortfolioForm;
	stockListForm;
	guidedTourIsActive = false;

	get hasFunds() {
		return _.get(this, "oldPortfolio.data.funds.length", 0) > 0;
	}

	get hasSecurities() {
		return _.get(this, "oldPortfolio.data.securities.length", 0) > 0;
	}

	get isGuidedComplete() {
		return (
			this.guidedTourIsActive &&
			this.oldBank.legacyBankIdentifier &&
			this.oldBank.oldAccountNumber
		);
	}

	get portfolioAccountIsAvailable() {
		return !!this.newBank.portfolioId;
	}

	get investmentAccountIsAvailable() {
		return !!this.newBank.investmentId;
	}

	get newAccountForPortfolioIsNeeded() {
		const showNewPortfolioNeededInfo = this.configService.isFeatureActive(
			this.Features.showNewPortfolioNeededInfo,
		);

		return (
			this.hasSecurities &&
			!this.portfolioAccountIsAvailable &&
			showNewPortfolioNeededInfo
		);
	}

	get newAccountForInvestmentIsNeeded() {
		return this.hasFunds && !this.investmentAccountIsAvailable;
	}

	get showHeadline() {
		return this.configService.isFeatureActive(this.Features.showHeadline);
	}

	get hasNoEntries() {
		return !this.hasFunds && !this.hasSecurities;
	}

	get hasRemainingPositionsToTransfer() {
		return (
			this.appCache.get("oldPortfolio").disposeSecuritiesAccount &&
			((this.hasFunds && !this.newPortfolio.transferAllFunds) ||
				(this.hasSecurities && !this.newPortfolio.transferAllSecurities))
		);
	}

	get transferAllSecurities() {
		return this.oldPortfolio.transferAllSecurities;
	}

	constructor(
		$scope,
		$q,
		controllerService,
		securitiesService,
		addSecuritiesService,
		uiModalDeclarationService,
		configService,
	) {
		"ngInject";
		super({
			$scope,
			$q,
			controllerService,
			securitiesService,
			addSecuritiesService,
			uiModalDeclarationService,
			configService,
		});
	}

	get doNotShowPriceColumns() {
		return this.guidedTourIsActive;
	}

	$onInit() {
		this.oldBank = this.appCache.get("oldBank") || new OldBankModel();
		this.newBank = this.appCache.get("newBank") || new NewBankModel();
		this.oldPortfolio =
			this.appCache.get("oldPortfolio") || new OldPortfolioModel();
		this.newPortfolio =
			this.appCache.get("newPortfolio") || new NewPortfolioModel();
		this.guidedTourIsActive = this.appCache.get("guidedTourIsActive");

		this.$scope.$on("stockListChanged", (scope, changes) => {
			this.appCache.set("oldPortfolio", this.oldPortfolio);
			this.appCache.set("newPortfolio", this.newPortfolio);
		});

		// if depot has fragments and user did not check sell fragments before show a info and the sell fragments box
		this.$scope.$on("hasFragments", (scope, changes) => {
			if (!this.oldPortfolio.sellFragments) {
				this.showFragmentsNotification = true;
				this.oldPortfolio.sellFragments = true;
			}
		});

		this.initState();
	}

	initState() {
		// Start guided tour if it is active
		if (this.guidedTourIsActive) {
			return this.startGuidedTour();
		}

		// Fetch securities with UBA if not all securities should be transferred
		if (!this.transferAllSecurities) {
			return this.fetchSecuritiesWithUBA();
		}
	}

	startGuidedTour() {
		this.guidedTourIsActive = true;

		// Initiatize empty portfolio lists
		if (!this.oldPortfolio.data) {
			this.oldPortfolio;
		}
		_.defaults(this.oldPortfolio, {
			data: {
				accounts: [],
				securities: [],
				funds: [],
			},
		});

		_.defaults(this.newPortfolio, {
			securities: [],
			funds: [],
		});

		this.appCache.set("guidedTourIsActive", this.guidedTourIsActive);
		this.appCache.set("oldPortfolio", this.oldPortfolio);
	}

	fetchSecuritiesWithUBA() {
		this.guidedTourIsActive = false;
		this.loading = true;
		const userIdentifier = this.appCache.get("UBAUserIdentifier");
		const accessTokenForTransactions = this.appCache.get(
			"accessTokenForTransactions",
		);

		if (!userIdentifier) {
			return;
		}

		this.securitiesService
			.fetchWithUBA(userIdentifier, accessTokenForTransactions)
			.then((response) => {
				// Handle empty response and load guided
				if (_.isEmpty(response.data.securities)) {
					this.showEmptyMessage = true;
					this.startGuidedTour();
					return;
				}

				const { accountNumber, legacyBankIdentifier } =
					response.data.accounts[0];

				this.oldPortfolio.data = angular.copy(response.data);
				this.newPortfolio = angular.copy(response.data);

				this.oldBank.oldAccountNumber = accountNumber;
				this.oldBank.legacyBankIdentifier = legacyBankIdentifier;
				this.updateCache();
			})
			.catch(() => {
				this.showEmptyMessage = true;
				this.startGuidedTour();
			})
			.finally(() => {
				this.loading = false;
			});
	}

	updateCache() {
		this.appCache.set("oldBank", this.oldBank);
		this.appCache.set("oldPortfolio", this.oldPortfolio);
		this.appCache.set(
			"transferAllSecurities",
			this.oldPortfolio.transferAllSecurities,
		);
	}

	next() {
		this.updateCache();

		if (this.oldPortfolioForm) {
			this.oldPortfolioForm.$setSubmitted();
			if (this.oldPortfolioForm.$invalid) return;
		}

		if (this.oldPortfolio.transferAllSecurities) {
			return this.nextState();
		}

		if (this.oldBankForm) {
			this.oldBankForm.$setSubmitted();
			if (!this.oldBankForm.$valid) {
				return;
			}
		}

		if (!this.hasDataToTransfer()) {
			this.informMissingTransferOfPositions();
			return;
		}

		if (this.stockListForm && this.stockListForm.$invalid) {
			return;
		}

		this.setNewPortfolioData();

		if (this.hasRemainingPositionsToTransfer) {
			this.informRemainingPositionsToTransfer()
				.then(() => this.cancelDisposingSecuritiesAccount())
				.catch(() => this.fillUpSecurities())
				.finally(() => this.nextState());
		} else {
			this.nextState();
		}
	}

	prev() {
		if (!this.oldPortfolio.transferAllSecurities) {
			return this.go("app.transfer");
		}

		// Don't know if and why we need this
		if (!this.hasFunds && !this.hasSecurities) {
			this.appCache.remove("oldPortfolio");
			this.oldPortfolio = null;
		}

		this.setNewPortfolioData();
		this.prevState();
	}

	fillUpSecurities() {
		_.mapKeys(
			this.newPortfolio.securities,
			(securitie, index) =>
				(securitie.quantity =
					this.oldPortfolio.data.securities[parseInt(index)].quantity),
		);
		this.setNewPortfolioData();
	}

	cancelDisposingSecuritiesAccount() {
		let oldPortfolio = this.appCache.get("oldPortfolio");
		oldPortfolio.disposeSecuritiesAccount = false;
		this.appCache.set("oldPortfolio", oldPortfolio);
	}

	setNewPortfolioData() {
		this.appCache.set("newPortfolio", this.newPortfolio);
	}

	setOldPortfolioData() {
		this.appCache.set("oldPortfolio", this.oldPortfolio);
	}

	hasDataToTransfer() {
		if (
			!this.newPortfolio ||
			(!this.newPortfolio.funds && !this.newPortfolio.securities)
		) {
			return false;
		}

		return this.hasSecurities || this.hasFunds;
	}

	informMissingTransferOfPositions() {
		this.$scope.confirmTitle = "app.newPortfolioState.confirm.title";
		this.$scope.confirmBody =
			"app.newPortfolioState.confirm.missing.positions.body";
		this.$scope.confirmOk = "app.confirmation.ok";
		this.$scope.showDismissBtn = false;

		this.uiModalDeclarationService.openUrl({
			url: "app/common/ui/modals/confirmation.tpl.html",
			options: {
				scope: this.$scope,
				modalClass: "confirmation-modal",
			},
		});
	}

	addSecurities() {
		this.addSecuritiesService.open().then((result) => {
			this.oldPortfolio.data.securities.push(result);
			this.newPortfolio.securities.push(angular.copy(result));
			this.setOldPortfolioData();
			this.setNewPortfolioData();
		});
	}

	informRemainingPositionsToTransfer() {
		const { $scope, $q } = this;
		$scope.confirmTitle = "app.newPortfolioState.confirm.title";
		$scope.confirmBody =
			"app.newPortfolioState.confirm.remaining.positions.body";
		$scope.showDismissBtn = true;
		$scope.confirmDismiss =
			"app.newPortfolioState.confirm.remaining.positions.dismiss";
		$scope.confirmOk =
			"app.newPortfolioState.confirm.remaining.positions.confirm";

		const defer = $q.defer();
		this.uiModalDeclarationService.openUrl({
			url: "app/common/ui/modals/confirmation.tpl.html",
			options: {
				scope: $scope,
				onSuccess: () => defer.resolve(),
				onDissmiss: () => defer.reject(),
				modalClass: "confirmation-modal",
			},
		});
		return defer.promise;
	}
}



// WEBPACK FOOTER //
// ./src/app/components/newPortfolio/newPortfolio.controller.js